import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LangContext";
import { t } from "@/i18n";
import { Sparkles, Crown, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const TIERS = [
  { id: "basic", price: 8.99, cap: "21" },
  { id: "advanced", price: 21.99, cap: "144", featured: true },
  { id: "ultimate", price: 55.99, cap: "∞" },
];

export default function PaywallDialog({ open, onOpenChange, limitKey, page = "unknown" }) {
  const { lang } = useLang();

  // Log analytics event when paywall opens
  useEffect(() => {
    if (open && limitKey) {
      api.post("/analytics/paywall", { limit_key: limitKey, page }).catch(() => { /* ignore */ });
    }
  }, [open, limitKey, page]);

  const messageMap = {
    ai_coach: t(lang, "limit_reached_ai"),
    journal_rewrite: t(lang, "limit_reached_rewrite"),
    exercise_locked: t(lang, "upgrade_to_unlock"),
  };
  const message = messageMap[limitKey] || t(lang, "limit_reached");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="paywall-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Crown className="w-6 h-6 text-accent" strokeWidth={1.5} />
            {t(lang, "unlock_premium")}
          </DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 py-2">
          {TIERS.map((tt) => (
            <div
              key={tt.id}
              data-testid={`paywall-tier-${tt.id}`}
              className={`p-3 rounded-lg border text-center ${
                tt.featured ? "border-accent bg-accent/10" : "border-border bg-card"
              }`}
            >
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{tt.id}</div>
              <div className="font-heading text-xl font-medium mt-1">${tt.price}</div>
              <div className="text-xs text-muted-foreground mt-1">{tt.cap} / {t(lang, "day")}</div>
            </div>
          ))}
        </div>

        <div className="space-y-2 pt-1">
          {["premium_feat_1", "premium_feat_2", "premium_feat_3"].map((k) => (
            <div key={k} className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-accent mt-0.5 shrink-0" strokeWidth={1.5} />
              <span className="text-sm">{t(lang, k)}</span>
            </div>
          ))}
        </div>

        <Link to="/app/plan" onClick={() => onOpenChange(false)}>
          <Button className="w-full rounded-full gap-2" data-testid="paywall-upgrade-btn">
            {t(lang, "upgrade_now")} <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </DialogContent>
    </Dialog>
  );
}
